import { Box, Typography } from "@mui/material";
import { GridColDef, GridEventListener } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid";
import dayjs from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useCallback, useState } from "react";
import { useFormatNumber } from "src/utils/formatNumber";
import { defaultTableSx } from "../tableFormatting";
import { useStrategyDashboardEditingRecipesByDate } from "./editingHooks";
import { RecipeEditsModal } from "./RecipeEditsModal";

type EditingRecipesTableProps = Readonly<{
  selectedDate: Date;
  selectedProductGroup: string;
}>;

export const EditingRecipesTable = ({
  selectedDate,
  selectedProductGroup,
}: EditingRecipesTableProps) => {
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber();

  const selectedDay = dayjs.utc(selectedDate);
  const formattedDate = selectedDay.format("YYYY-MM-DD");

  const { data, isFetching } = useStrategyDashboardEditingRecipesByDate(
    formattedDate,
    selectedProductGroup
  );

  const recipes = data?.recipes ?? [];

  const [selectedRecipe, setSelectedRecipe] = useState<string | null>(null);

  const handleRowClick: GridEventListener<"rowClick"> = useCallback(
    (params: { row: { recipe_code: string } }) => {
      setSelectedRecipe(params.row.recipe_code);
    },
    []
  );

  const handleCloseModal = useCallback(() => {
    setSelectedRecipe(null);
  }, []);

  const columns: GridColDef[] = [
    {
      field: "recipe_code",
      headerName: t("recipeCode"),
      flex: 1,
      minWidth: 150,
    },
    {
      field: "num_heats_edited",
      headerName: t("heats"),
      type: "number",
      flex: 0.5,
      minWidth: 100,
    },
    {
      field: "recipe_mass_changed_tons",
      headerName: `${t("recipeMassChanged")} (${t("tons")})`,
      type: "number",
      flex: 1,
      minWidth: 180,
      valueFormatter: (value: number) => formatNumber(1)(value),
    },
  ];

  return (
    <Box sx={{ width: "100%", mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("recipeBreakdownFor")} {selectedDay.format("MMM D, YYYY")} -{" "}
        {selectedProductGroup}
      </Typography>
      <HotkeyCompliantDataGrid
        rows={recipes}
        columns={columns}
        loading={isFetching}
        getRowId={(row: { recipe_code: string }) => row.recipe_code}
        onRowClick={handleRowClick}
        autoHeight
        hideFooter={recipes.length <= 10}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        pageSizeOptions={[10, 25, 50]}
        sx={{
          ...defaultTableSx,
          "& .MuiDataGrid-row": { cursor: "pointer" },
        }}
      />
      {selectedRecipe ? (
        <RecipeEditsModal
          open={!!selectedRecipe}
          onClose={handleCloseModal}
          recipeCode={selectedRecipe}
          heatDate={selectedDate}
        />
      ) : null}
    </Box>
  );
};
